import type { SeoLocale } from "./seoTypes";
import type { GuidePageKey } from "@/lib/site/publicPages";

/**
 * Facts about Zettl that machine readers get verbatim: JSON-LD, llms.txt and
 * llms-full.txt. Every line must be true of the product today. Check the code
 * before adding one, and never add numbers, customers or ratings that aren't
 * published elsewhere.
 */

export const BRAND_NAME = "Zettl";

/** Where customers write for help: on /contact, routed to the team's mailbox. */
export const SUPPORT_EMAIL = "podpora@zettl.cz";

/** The operator, as published on /contact and in the footer. */
export const OPERATOR = {
  legalName: "IDEx Corporate s.r.o.",
  ico: "18002161",
  vatId: "CZ18002161",
  email: "info@idex-corp.com",
  address: {
    streetAddress: "Kurzova 2222/16",
    addressLocality: "Praha",
    postalCode: "155 00",
    addressCountry: "CZ",
  },
} as const;

/** One plain definitional sentence per locale: the line AI answers should quote. */
export const DEFINITION: Record<SeoLocale, string> = {
  cs: "Zettl je online aplikace pro s.r.o. a OSVČ s účtem u Fio banky: přes Fio API stahuje platby z vašeho účtu a ke každé automaticky hledá účtenku nebo fakturu.",
  en: "Zettl is a web app for Czech companies and freelancers (OSVČ) banking with Fio banka: it pulls payments through the Fio API and automatically looks for each one's receipt or invoice.",
};

/** Short feature names for `SoftwareApplication.featureList`. */
export const FEATURES: Record<SeoLocale, string[]> = {
  cs: [
    "Napojení na Fio banku přes Fio API",
    "Automatické párování plateb s účtenkami a fakturami",
    "Vlastní e-mailová adresa @zettl.cz pro doklady každé firmy",
    "AI vytěžení dodavatele, data a částky z dokladu",
    "Import výpisů ve formátu GPC/ABO",
    "Podklady pro účetní: měsíční PDF balíček dokladů, export CSV, XLSX a ISDOC, export pro POHODU (XML)",
    "Stránka Chybějící doklady a pondělní e-mail o platbách bez dokladu",
    "Vystavování faktur s QR platbou",
    "Firemní a soukromý režim, rozpočty a přehledy",
    "MCP konektor pro AI asistenty (např. Claude)",
  ],
  en: [
    "Fio banka connection through the Fio API",
    "Automatic matching of payments with receipts and invoices",
    "Each organization's own @zettl.cz e-mail address for documents",
    "AI extraction of vendor, date and amount",
    "GPC/ABO bank statement import",
    "Files for your accountant: monthly PDF bundle of documents, CSV, XLSX and ISDOC export, export for POHODA (XML)",
    "A Missing documents page and a Monday e-mail about payments without a document",
    "Invoicing with QR payment codes",
    "Business and private mode, budgets and analytics",
    "MCP connector for AI assistants (e.g. Claude)",
  ],
};

/**
 * Longer fact sections for llms-full.txt, in Czech and English (the two languages
 * AI answers about a Czech product are asked in most).
 */
export interface FactSection {
  heading: string;
  lines: string[];
}

export const FACT_SECTIONS: Record<"cs" | "en", FactSection[]> = {
  cs: [
    {
      heading: "Co Zettl je",
      lines: [
        DEFINITION.cs,
        "Zettl je určený pro české firmy (s.r.o.) a OSVČ, které mají podnikatelský účet u Fio banky.",
        "Zettl provozuje IDEx Corporate s.r.o., IČO 18002161, Kurzova 2222/16, 155 00 Praha.",
        "Aplikace běží v prohlížeči na app.zettl.cz, česky a anglicky.",
      ],
    },
    {
      heading: "Jak funguje napojení na Fio banku",
      lines: [
        "V internetovém bankovnictví Fio vytvoříte API token (Nastavení → API) s oprávněním „Sledování účtu“ a vložíte ho do Zettl (Nastavení → Integrace).",
        "Zettl pak každý den sám stáhne nové pohyby na účtu.",
        "Zettl volá jen ta rozhraní Fio API, která čtou pohyby a výpisy. Funkci pro zadání platby nemá.",
        "Starší výpisy, z doby před napojením, nahrajete ručně ve formátu GPC/ABO. Platbu, kterou už Zettl má z Fio API, výpis GPC/ABO nepřidá podruhé.",
        "Zettl podporuje jen Fio banku. Jiné banky napojit nelze.",
      ],
    },
    {
      heading: "Jak se doklady dostanou do Zettl",
      lines: [
        "Každá firma v Zettlu má vlastní e-mailovou adresu @zettl.cz (zapnete ji v Nastavení → Banky a integrace). Účtenku nebo fakturu na ni přepošlete.",
        "Doklady lze také nahrát přímo v aplikaci (PDF nebo fotka).",
        "AI z dokladu vyčte dodavatele, datum a částku.",
      ],
    },
    {
      heading: "Párování plateb s doklady",
      lines: [
        "Zettl ke každé platbě z Fio banky hledá odpovídající účtenku nebo fakturu podle částky, data a dodavatele.",
        "Platby bez dokladu zůstanou viditelně nespárované, takže víte, co ještě chybí.",
        "Zettl spáruje sám, vy zkontrolujete a jedním klikem zrušíte. Spárovat lze i ručně.",
      ],
    },
    {
      heading: "Pro účetní",
      lines: [
        "Za každý měsíc lze stáhnout PDF balíček dokladů; vydané faktury se exportují jako XLSX nebo ISDOC, platby jako CSV a výpis z Fio jako PDF.",
        "Export pro POHODU: přijaté doklady a bankovní pohyby za měsíc jako XML pro import do POHODY (verze 2, Windows-1250) se soubory dokladů v jednom ZIPu. Doklady přijdou celkovou částkou, bez rozdělení podle sazeb; vyžaduje IČO firmy.",
        "Je-li POHODA mServer dostupný z internetu přes HTTPS, pošle Zettl měsíc do POHODY i přímo, s výsledkem u každého dokladu; opakované odeslání přeskočí, co už v POHODĚ je.",
        "Stránka Chybějící doklady ukazuje odchozí platby bez dokladu; vlastník a správci firmy dostávají v pondělí e-mail s platbami bez dokladu za uplynulý týden.",
        "Vydané faktury lze importovat ze souborů ISDOC, i jako ZIP z exportu Shoptetu (Objednávky → Daňové doklady → Export → XML (ISDOC)); příchozí platby se k nim hned spárují podle variabilního symbolu. Tarif Firma.",
        "E-shop na Shoptetu s tarifem Shoptet Premium napojíte soukromým API tokenem (Nastavení → Banky a integrace): vydané faktury pak přicházejí automaticky každou noc nebo na tlačítko Synchronizovat nyní, párují se s platbami z Fio podle variabilního symbolu a faktura už nahraná přes ISDOC se nezdvojí. Ostatní tarify Shoptetu používají import ISDOC ZIP.",
        "Výplatní reporty Comgate a Zásilkovny (CSV, XLSX): každá výplata se spojí s příchozí platbou na Fio, faktury, které pokrývá, se označí jako zaplacené a zaznamená se hrubá částka, poplatek a čistá výplata.",
        "Do organizace v Zettl lze pozvat další členy, například účetní.",
        "Podklady e-mailem: vlastník nebo správce firmy zapne v Nastavení → Účetnictví den v měsíci (1.–28.) a až pět adres; ten den Zettl pošle odkaz (platný 14 dní) na ZIP za předchozí měsíc s výpisem z účtu jako CSV, soubory dokladů a XML pro POHODU.",
        "Účetní, který je členem firem více klientů, vidí na stránce Klienti všechny firmy za zvolený měsíc: počet plateb bez dokladu, stav Fio tokenu, poslední data a odeslání do POHODY. Tlačítko Připomenout klientovi pošle vlastníkovi a správcům firmy e-mail s platbami bez dokladu, nejvýš jednou denně.",
      ],
    },
    {
      heading: "Navíc",
      lines: [
        "Vystavování faktur se šablonami a QR platbou; při nahrání výpisu GPC/ABO se platba s variabilním symbolem faktury sama připojí k faktuře.",
        "Firemní a soukromý režim, rozpočty a přehledy příjmů a výdajů.",
      ],
    },
    {
      heading: "AI asistenti přes MCP",
      lines: [
        "Zettl je vzdálený MCP server na adrese https://zettlapp.com/mcp. Do AI asistenta s podporou MCP (např. Claude) ho přidáte touto jednou adresou.",
        "Připojení je přes OAuth 2.1: přihlásíte se svým účtem Zettl a na obrazovce souhlasu povolíte přístup k jedné organizaci.",
        "Oprávnění zettl:read: asistent čte faktury, platby, doklady, klienty a připojené účty (nikdy přihlašovací údaje), najde platby bez dokladu a shrne příjmy a výdaje.",
        "Oprávnění zettl:write udělíte jen zaškrtnutím „Povolit i změny dat“. Asistent pak může spárovat doklad s platbou, spárování zrušit, nastavit kategorii platby a změnit stav faktury. Bez něj je připojení jen pro čtení.",
        "Asistent jedná s vašimi právy a vidí jen organizaci, ke které jste přístup povolili.",
        "Připojení zrušíte v Nastavení → Banky a integrace → AI připojení; zrušení platí okamžitě.",
        "V ceníku je MCP konektor součástí tarifu Firma.",
      ],
    },
    {
      heading: "Bezpečnost a ochrana dat",
      lines: [
        "API token k Fio je uložený šifrovaně (AES-256-GCM) a dešifruje se jen uvnitř serverových funkcí Zettl při synchronizaci.",
        "Data jednotlivých organizací odděluje zabezpečení na úrovni řádků (Row Level Security).",
      ],
    },
  ],
  en: [
    {
      heading: "What Zettl is",
      lines: [
        DEFINITION.en,
        "Zettl is built for Czech limited companies (s.r.o.) and sole traders (OSVČ) with a business account at Fio banka.",
        "Zettl is operated by IDEx Corporate s.r.o., company ID (IČO) 18002161, Kurzova 2222/16, 155 00 Prague, Czech Republic.",
        "It runs in the browser at app.zettl.cz, in Czech and English.",
      ],
    },
    {
      heading: "How the Fio banka connection works",
      lines: [
        "You create an API token in Fio internet banking (Settings → API) with the “Account monitoring” permission and paste it into Zettl (Settings → Integrations).",
        "From then on Zettl downloads new account movements every day on its own.",
        "Zettl only calls the parts of the Fio API that read transactions and statements. It has no function for sending payments.",
        "Older statements from before the connection can be uploaded in GPC/ABO format. A GPC/ABO statement does not add a payment Zettl already has from the Fio API a second time.",
        "Zettl supports Fio banka only. Other banks cannot be connected.",
      ],
    },
    {
      heading: "How documents get into Zettl",
      lines: [
        "Each organization gets its own @zettl.cz e-mail address, shared by its members (switched on in Settings → Banks & integrations). Forward a receipt or invoice to it.",
        "Documents can also be uploaded in the app (PDF or photo).",
        "AI reads the vendor, date and amount from each document.",
      ],
    },
    {
      heading: "Matching payments with documents",
      lines: [
        "For every Fio banka payment Zettl looks for the matching receipt or invoice by amount, date and vendor.",
        "Payments without a document stay visibly unmatched, so you see what is still missing.",
        "Zettl matches on its own; you review the result and undo a match with one click. Matches can also be made by hand.",
      ],
    },
    {
      heading: "For accountants",
      lines: [
        "A monthly PDF bundle of documents can be downloaded; issued invoices export as XLSX or ISDOC, payments as CSV, and the Fio statement as PDF.",
        "Export for POHODA: a month's received documents and bank movements as XML for import into POHODA (version 2, Windows-1250), with the document files, in one ZIP. Documents arrive as their total, not split by rate; the company's IČO is required.",
        "If POHODA mServer is reachable from the internet over HTTPS, Zettl can also send the month straight into POHODA, with a result per document; a repeated send skips what POHODA already has.",
        "The Missing documents page lists outgoing payments without a document; the company's owner and admins get a Monday e-mail with last week's payments without a document.",
        "Issued invoices can be imported from ISDOC files, including a ZIP from Shoptet's export (Orders → Tax documents → Export → XML (ISDOC)); incoming payments are matched to them by variable symbol straight away. Firma plan.",
        "A Shoptet e-shop on the Shoptet Premium plan connects with its private API token (Settings → Banks & integrations): issued invoices then come in automatically every night or on Sync now, are matched to Fio payments by variable symbol, and an invoice already imported via ISDOC is not duplicated. Other Shoptet plans use the ISDOC ZIP import.",
        "Comgate and Packeta (Zásilkovna) payout reports (CSV, XLSX): each payout is linked to the incoming Fio payment, the invoices it covers are marked paid, and the gross amount, fee and net payout are recorded.",
        "Other people, such as your accountant, can be invited to the organization in Zettl.",
        "Documents by e-mail: the company's owner or an admin picks a day of the month (1st–28th) and up to five addresses in Settings → Accounting; on that day Zettl sends a link (valid for 14 days) to a ZIP for the previous month with the account statement as CSV, the documents' files and the XML for POHODA.",
        "An accountant who is a member of several clients' companies sees all of them for the chosen month on the Clients page: payments without a document, Fio token health, latest data and POHODA sends. Remind the client e-mails the company's owner and admins the payments without a document, at most once a day.",
      ],
    },
    {
      heading: "Extras",
      lines: [
        "Invoicing with templates and QR payment codes; when a GPC/ABO statement is uploaded, a payment carrying an invoice's variable symbol is linked to that invoice automatically.",
        "Business and private mode, budgets, income and expense analytics.",
      ],
    },
    {
      heading: "AI assistants via MCP",
      lines: [
        "Zettl is a remote MCP server at https://zettlapp.com/mcp. You add it to an MCP-capable AI assistant (e.g. Claude) with that one address.",
        "Connecting uses OAuth 2.1: you sign in with your Zettl account and, on the consent screen, grant access to one organization.",
        "Scope zettl:read: the assistant reads invoices, payments, documents, clients and connected accounts (never credentials), finds payments without a document and summarizes income and expenses.",
        "Scope zettl:write is granted only by ticking “Also allow changes”. The assistant can then match a document to a payment, undo a match, set a payment's category and change an invoice's status. Without it the connection is read-only.",
        "The assistant acts with your permissions and sees only the organization you granted.",
        "Revoke a connection under Settings → Banks & integrations → AI connections; revocation takes effect immediately.",
        "In the pricing, the MCP connector is part of the Firma plan.",
      ],
    },
    {
      heading: "Security and data protection",
      lines: [
        "The Fio API token is stored encrypted (AES-256-GCM) and decrypted only inside Zettl's server functions during sync.",
        "Row Level Security keeps each organization's data separate.",
      ],
    },
  ],
};

/**
 * The free tools on the public site (/nastroje-zdarma): one plain fact each,
 * for the "Free tools" sections of llms.txt and llms-full.txt. They run in the
 * visitor's browser only; a tool that sends anything anywhere must say so here.
 */
export const FREE_TOOLS_INTRO: Record<SeoLocale, string> = {
  cs: "Nástroje zdarma na webu Zettlu běží jen v prohlížeči návštěvníka: soubor se nikam neodesílá, nic se neukládá a nepotřebují registraci.",
  en: "The free tools on Zettl's website run only in the visitor's browser: the file is never sent anywhere, nothing is stored and no sign-up is needed.",
};

export const FREE_TOOL_FACTS: { key: GuidePageKey; line: Record<SeoLocale, string> }[] = [
  {
    key: "toolGpcToCsv",
    line: {
      cs: "Převod výpisu GPC / ABO do CSV a Excelu (XLSX): výpis z Fio banky (záznamy 074 a 075, kódování windows-1250) převede na jeden řádek na pohyb, s náhledem.",
      en: "GPC / ABO to CSV and Excel (XLSX): turns a Fio banka statement (records 074 and 075, windows-1250) into one row per movement, with a preview.",
    },
  },
  {
    key: "toolIsdocViewer",
    line: {
      cs: "Prohlížeč faktur ISDOC: otevře soubor .isdoc nebo .isdocx jako čitelnou fakturu (strany, položky, souhrn daně podle sazeb, platební údaje, QR platba u faktur v korunách) s tiskem a uložením do PDF.",
      en: "ISDOC invoice viewer: opens an .isdoc or .isdocx file as a readable invoice (parties, items, tax summary by rate, payment details, a QR payment for CZK invoices) with print and save as PDF.",
    },
  },
  {
    key: "toolQrPayment",
    line: {
      cs: "Generátor QR platby: z českého čísla účtu (ověřeného kontrolním součtem) nebo IBAN CZ/SK, částky, symbolů, splatnosti a zprávy vytvoří QR platbu SPD 1.0 ke stažení jako PNG nebo SVG.",
      en: "QR payment generator: makes an SPD 1.0 Czech QR payment from a Czech account number (checked by its check digits) or a CZ/SK IBAN, amount, symbols, due date and message, to download as PNG or SVG.",
    },
  },
  {
    key: "toolAccountIban",
    line: {
      cs: "Převod čísla účtu na IBAN a zpět: ověří kontrolní číslice (modulo 11 podle vyhlášky ČNB, IBAN modulo 97), doplní banku a BIC z číselníku kódů bank ČNB; až 1 000 účtů najednou s exportem do CSV.",
      en: "Account number to IBAN and back: checks the check digits (modulo 11 under the Czech National Bank's decree, IBAN modulo 97) and adds the bank and BIC from the CNB's bank code list; up to 1,000 accounts at once with a CSV export.",
    },
  },
  {
    key: "toolGpcPohoda",
    line: {
      cs: "Výpis GPC do POHODY: výpis GPC / ABO z Fio převede na XML import bankovních pohybů do POHODY (agenda Banka, schémata Stormware verze 2, kódování Windows-1250) pro zadané IČO.",
      en: "GPC statement to POHODA: turns a Fio GPC / ABO statement into an XML import of bank movements for POHODA (Bank agenda, Stormware version 2 schemas, Windows-1250) for the given company ID.",
    },
  },
];
